/**
 * How fast the running loop is playing, and how loud.
 *
 * Pure, and deliberately not part of `WebAudioManager`, for the same reason
 * `karaoke.ts` is not: the interesting part of a sound that follows the player
 * is the arithmetic, not the node graph, and the arithmetic is worth testing
 * without a browser. The mixer's only job is to follow what this returns.
 *
 * Presentation only. Nothing here is reachable from `src/sim`.
 */

import { RUN_LOOP } from '../config/audio';
import { clamp } from '../utils/math';

export interface RunMix {
  /** 0 - 1, multiplied into the cue's usual gain. */
  gain: number;
  /** `playbackRate` for the loop. 1 = the take running at its own cadence. */
  rate: number;
}

/**
 * Ground speed in m/s to the pace the feet are heard at.
 *
 * The rate is a fractional power of the speed ratio rather than the ratio
 * itself, because a runner going twice as fast is not taking steps twice as
 * fast - most of the difference is stride length. Playing the take at double
 * rate would also raise it an octave, which is the difference between a person
 * running and a person being rewound.
 */
export function runMix(speed: number): RunMix {
  const reach = clamp(
    (speed - RUN_LOOP.quietSpeed) / (RUN_LOOP.loudSpeed - RUN_LOOP.quietSpeed),
    0,
    1,
  );
  // Standing still is silent, and the fade up happens over the first few steps
  // rather than the first few centimetres.
  const gain = reach * reach;

  const ratio = Math.max(speed, 0) / RUN_LOOP.referenceSpeed;
  const rate =
    ratio <= 0
      ? RUN_LOOP.minRate
      : clamp(Math.pow(ratio, RUN_LOOP.rateFollow), RUN_LOOP.minRate, RUN_LOOP.maxRate);

  return { gain, rate };
}

/** What the mixer plays before the game has said how fast anybody is going. */
export const STILL_MIX: RunMix = { gain: 0, rate: 1 };
